import type { Immutable } from 'immer';
import { AuthorizationStates, ModulesStates, VisibilityStates } from '../consts/events';
import type { EventDocument, OptionModules, RacemapEvent } from '../types/events';
import type { EventTrackObject, PointObject, SplitObject } from '../types/geos';
import { RacemapGeoTypes } from '../types/geos';
import { isRacemapTrack, isSplitPoint } from './validation';

// event checks

export function isHiddenChildEvent(
  event: Pick<Immutable<RacemapEvent>, 'visibility' | 'authorization'>,
): boolean {
  return (
    event.visibility === VisibilityStates.DRAFT ||
    event.visibility === VisibilityStates.UNLISTED ||
    isNotActivatedEvent(event) ||
    isArchivedEvent(event)
  );
}

export function isNotActivatedEvent(
  event: Pick<Immutable<RacemapEvent>, 'authorization'>,
): boolean {
  return event.authorization === AuthorizationStates.NONE;
}

export function isArchivedEvent(event: Pick<Immutable<RacemapEvent>, 'visibility'>): boolean {
  return event.visibility === VisibilityStates.ARCHIVED;
}

export function calculateActivatedPeriods(
  dateEnd: Date | string | null,
  dateStart: Date | string | null,
): number {
  const date1 = dateEnd != null ? new Date(dateEnd) : new Date();
  const date2 = dateStart != null ? new Date(dateStart) : new Date();
  const years = new Date(date1).getFullYear() - new Date(date2).getFullYear();
  const month = new Date(date1).getMonth() - new Date(date2).getMonth();
  const days = new Date(date1).getDate() - new Date(date2).getDate();
  const activatedPeriods = Math.ceil((years * 12 + month + (days > 0 ? 1 : 0)) / 3);
  return activatedPeriods;
}

export function currentEventTimes(
  event?:
    | Immutable<{ startTime: string; endTime: string; isRepeating: boolean }>
    | { startTime: Date; endTime: Date; isRepeating: boolean }
    | null,
): [number | undefined, number | undefined, number | undefined] {
  if (!event?.startTime || !event?.endTime) return [undefined, undefined, undefined];
  const startTime =
    typeof event.startTime === 'string' ? Date.parse(event.startTime) : event.startTime.getTime();
  const endTime =
    typeof event.endTime === 'string' ? Date.parse(event.endTime) : event.endTime.getTime();
  const eventDuration = endTime - startTime;
  const repeatTimeOffset = event.isRepeating
    ? Math.floor((Date.now() - startTime) / eventDuration) * eventDuration
    : 0;
  return [startTime + repeatTimeOffset, endTime + repeatTimeOffset, repeatTimeOffset];
}

export function getTimekeepingSplits(
  event?: Immutable<RacemapEvent> | null,
): Immutable<Array<SplitObject>> {
  return getSplitsOfTrack(event).filter((split) => split.properties.timekeeping);
}

export function getSplitsOfTrack(
  event?: Immutable<{ geo: RacemapEvent['geo'] }> | null,
): Array<SplitObject> {
  if (
    event == null ||
    event.geo == null ||
    event.geo.shadowtrackId == null ||
    event.geo.features == null
  )
    return [];
  const trackId = event.geo.shadowtrackId;
  return event.geo.features
    .filter(isSplitPoint)
    .filter((f) => f.properties.lineStringId === trackId)
    .sort((a, b) =>
      a.properties.offset == null
        ? 1
        : b.properties.offset == null
          ? 1
          : a.properties.offset - b.properties.offset,
    );
}

export function getPointsOfInterest(
  event?: Immutable<{ geo: RacemapEvent['geo'] }> | null,
): Array<PointObject> {
  if (event?.geo?.features == null) return [];
  return event.geo.features.filter(
    (f) => f.properties.racemapType === RacemapGeoTypes.POI,
  ) as Array<PointObject>;
}

export function getSubReaderIds(splits: Immutable<Array<SplitObject>>): Set<string> {
  return new Set(
    splits.reduce<Array<string>>((prev, s) => {
      if (s.properties.readerIds == null) return prev;
      return prev.concat(s.properties.readerIds);
    }, []),
  );
}

export function getTracksOfEvent(event: Immutable<RacemapEvent>): Array<EventTrackObject> {
  const features = event.geo?.features;
  if (features == null) return [];

  return features.filter(isRacemapTrack);
}

export function generateStarterDeviceIdsString(
  event?: Immutable<RacemapEvent> | null,
): Array<string> {
  if (!event) return [];

  return Array.from(event.starters?.entries() || [])
    .map(([_, starter]) => starter.deviceId)
    .filter((id): id is string => id !== null);
}

export function isActivated(
  moduleOptions?:
    | OptionModules[keyof OptionModules]
    | EventDocument['modules'][keyof EventDocument['modules']]
    | Immutable<OptionModules[keyof OptionModules]>,
): boolean {
  if (moduleOptions == null || Array.isArray(moduleOptions)) return false;
  if ('state' in moduleOptions && moduleOptions.state != null)
    return moduleOptions.state === ModulesStates.ACTIVATED;
  if ('enabled' in moduleOptions && moduleOptions.enabled != null) return moduleOptions.enabled;
  return false;
}

export function isEnabled(
  moduleOptions?:
    | OptionModules[keyof OptionModules]
    | EventDocument['modules'][keyof EventDocument['modules']]
    | Immutable<OptionModules[keyof OptionModules]>,
): boolean {
  if (moduleOptions == null || Array.isArray(moduleOptions)) return false;
  if ('state' in moduleOptions && moduleOptions.state != null)
    return moduleOptions.state === ModulesStates.ENABLED;
  if ('enabled' in moduleOptions && moduleOptions.enabled != null) return moduleOptions.enabled;
  return false;
}
